import { useState } from "react";
import { useMutation } from "@apollo/client";
import { Loader2, ArrowRight, FileUp } from "@credcore/core-ui/icons";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn-components/Card";
import { Button } from "@/components/shadcn-components/Button";
import { Input } from "@/components/shadcn-components/Input";
import { Label } from "@/components/shadcn-components/Label";
import { Switch } from "@/components/shadcn-components/Switch";
import { RUN_INGESTION } from "@/graphql/queries";

type IngestionMode = "INCREMENTAL" | "BACKFILL" | "ARTIFACT";

interface IngestionFormState {
  mode: IngestionMode;
  windowMinutes: string;
  artifactId: string;
  fromDate: string;
  toDate: string;
  chunkDuration: string;
  dryRun: boolean;
}

interface IngestionResultData {
  success: boolean;
  runId?: string;
  mode: IngestionMode;
  windowStart?: string;
  windowEnd?: string;
  factsCreated: number;
  duration: number;
  status: string;
  errorMessage?: string;
  dryRun: boolean;
  estimatedRecords?: number;
}

const INITIAL_FORM_STATE: IngestionFormState = {
  mode: "INCREMENTAL",
  windowMinutes: "15",
  artifactId: "",
  fromDate: "",
  toDate: "",
  chunkDuration: "",
  dryRun: false,
};

export function IngestionForm() {
  const [formState, setFormState] =
    useState<IngestionFormState>(INITIAL_FORM_STATE);
  const [result, setResult] = useState<IngestionResultData | null>(null);

  const [runIngestion, { loading: isRunning }] = useMutation(RUN_INGESTION, {
    onCompleted: (data) => {
      setResult(data.runIngestion);
    },
    onError: (error) => {
      setResult({
        success: false,
        mode: formState.mode,
        factsCreated: 0,
        duration: 0,
        status: "failed",
        errorMessage: error.message,
        dryRun: formState.dryRun,
      });
    },
  });

  const handleModeChange = (mode: IngestionMode) => {
    setFormState((prev) => ({ ...prev, mode }));
    setResult(null);
  };

  const handleInputChange = (
    field: keyof IngestionFormState,
    value: string | boolean,
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const input: Record<string, unknown> = {
      mode: formState.mode,
      dryRun: formState.dryRun,
    };

    if (formState.mode === "INCREMENTAL") {
      input.windowMinutes = parseInt(formState.windowMinutes) || 15;
    } else if (formState.mode === "BACKFILL") {
      input.from = formState.fromDate;
      input.to = formState.toDate;
      if (formState.chunkDuration) {
        input.chunkDuration = formState.chunkDuration;
      }
    } else if (formState.mode === "ARTIFACT") {
      input.artifactId = formState.artifactId;
    }

    runIngestion({ variables: { input } });
  };

  const isSubmitDisabled =
    isRunning ||
    (formState.mode === "BACKFILL" &&
      (!formState.fromDate || !formState.toDate)) ||
    (formState.mode === "ARTIFACT" && !formState.artifactId);

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUp className="h-5 w-5" />
          Data Ingestion
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <div className="flex-1 space-y-4">
          <div className="space-y-2">
            <Label>Ingestion Mode</Label>
            <div className="flex gap-2">
              {(["INCREMENTAL", "BACKFILL", "ARTIFACT"] as IngestionMode[]).map(
                (mode) => (
                  <button
                    key={mode}
                    onClick={() => handleModeChange(mode)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      formState.mode === mode
                        ? "bg-gray-900 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {mode.charAt(0) + mode.slice(1).toLowerCase()}
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="min-h-[140px]">
            {formState.mode === "INCREMENTAL" && (
              <div className="space-y-2">
                <Label htmlFor="windowMinutes">Time Window (minutes)</Label>
                <Input
                  id="windowMinutes"
                  type="number"
                  value={formState.windowMinutes}
                  onChange={(e) =>
                    handleInputChange("windowMinutes", e.target.value)
                  }
                  placeholder="15"
                />
              </div>
            )}

            {formState.mode === "BACKFILL" && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="fromDate">From Date</Label>
                    <Input
                      id="fromDate"
                      type="datetime-local"
                      value={formState.fromDate}
                      onChange={(e) =>
                        handleInputChange("fromDate", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="toDate">To Date</Label>
                    <Input
                      id="toDate"
                      type="datetime-local"
                      value={formState.toDate}
                      onChange={(e) =>
                        handleInputChange("toDate", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chunkDuration">
                    Chunk Duration (optional)
                  </Label>
                  <Input
                    id="chunkDuration"
                    value={formState.chunkDuration}
                    onChange={(e) =>
                      handleInputChange("chunkDuration", e.target.value)
                    }
                    placeholder="e.g., 1d, 6h, 30m"
                  />
                </div>
              </div>
            )}

            {formState.mode === "ARTIFACT" && (
              <div className="space-y-2">
                <Label htmlFor="artifactId">Artifact ID</Label>
                <Input
                  id="artifactId"
                  type="number"
                  value={formState.artifactId}
                  onChange={(e) =>
                    handleInputChange("artifactId", e.target.value)
                  }
                  placeholder="Enter artifact ID"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="dryRun"
              checked={formState.dryRun}
              onCheckedChange={(checked) =>
                handleInputChange("dryRun", checked)
              }
            />
            <Label htmlFor="dryRun">Dry Run (preview only)</Label>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="w-full"
          >
            {isRunning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <ArrowRight className="mr-2 h-4 w-4" />
                Run Ingestion
              </>
            )}
          </Button>

          {result && (
            <div
              className={`rounded-md p-4 ${
                result.success
                  ? "bg-green-50 text-green-800"
                  : "bg-red-50 text-red-800"
              }`}
            >
              <h4 className="font-medium">
                {result.dryRun ? "Dry Run Result" : "Ingestion Result"}
              </h4>
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt>Status:</dt>
                  <dd className="font-medium">{result.status}</dd>
                </div>
                {result.runId && (
                  <div className="flex justify-between">
                    <dt>Run ID:</dt>
                    <dd className="font-mono text-xs">{result.runId}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt>
                    {result.dryRun ? "Estimated Records:" : "Facts Created:"}
                  </dt>
                  <dd className="font-medium">
                    {result.dryRun
                      ? result.estimatedRecords
                      : result.factsCreated}
                  </dd>
                </div>
                {!result.dryRun && result.duration > 0 && (
                  <div className="flex justify-between">
                    <dt>Duration:</dt>
                    <dd>{(result.duration / 1000).toFixed(2)}s</dd>
                  </div>
                )}
                {result.errorMessage && (
                  <div className="mt-2 text-red-700">
                    <dt className="font-medium">Error:</dt>
                    <dd>{result.errorMessage}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
